import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GitCompare, Stethoscope, MessageSquare } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import LoadingSpinner from '../common/LoadingSpinner';
import MetricsFilters from './MetricsFilters';
import { VERDICT_LABELS, useMetricsOverview } from '../../hooks/useMetricsApi';

const SOURCE_ICONS = {
  clinician: Stethoscope,
  self_report: MessageSquare,
};

const InstrumentCard = ({ instrument }) => {
  const Icon = SOURCE_ICONS[instrument.source] || Stethoscope;
  const withheld = Object.entries(instrument.verdicts)
    .filter(([verdict]) => verdict !== 'reportable')
    .sort((left, right) => right[1] - left[1]);

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex-1 p-5">
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <div>
            <h3 className="text-base font-medium text-slate-900">{instrument.label}</h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {instrument.source === 'clinician'
                ? 'Completed by a therapist'
                : 'Completed by the student'}
            </p>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-3 gap-2">
          <div>
            <dt className="text-xs text-slate-500">Semesters</dt>
            <dd className="text-lg font-semibold text-slate-900">{instrument.episodes}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Students</dt>
            <dd className="text-lg font-semibold text-slate-900">{instrument.students}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Reportable</dt>
            <dd className="text-lg font-semibold text-slate-900">
              {instrument.reportableItems}
              <span className="text-sm font-normal text-slate-400">
                /{instrument.totalItems}
              </span>
            </dd>
          </div>
        </dl>

        {withheld.length > 0 && (
          <ul className="mt-3 space-y-0.5">
            {withheld.map(([verdict, count]) => (
              <li key={verdict} className="text-xs text-slate-500">
                {count} {VERDICT_LABELS[verdict]?.toLowerCase() || verdict}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-slate-50 px-5 py-3">
        <Link
          to={instrument.key}
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          View all items
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};

const MetricsOverview = () => {
  const [filters, setFilters] = useState({ comparison: 'from_intake', sequence: '', since: '' });
  const { data, isLoading, error } = useMetricsOverview(filters);

  return (
    <div className="space-y-6 p-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Program performance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Aggregate change across each assessment the program collects. Figures
          cover cohorts, never individual students, and an item is reported only
          when the sample is large enough to say something about it.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <MetricsFilters filters={filters} onChange={setFilters} />
      </div>

      {isLoading && (
        <div className="p-8">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not load metrics</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {data.instruments.map((instrument) => (
              <InstrumentCard key={instrument.key} instrument={instrument} />
            ))}
          </div>

          <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="flex items-start gap-3 p-5">
              <GitCompare className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
              <div>
                <h3 className="text-base font-medium text-slate-900">
                  Do the two instruments agree?
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  The clinician assessment and the student survey cover the same
                  semesters. Comparing their verdicts shows whether an observed
                  improvement is one the student also felt.
                </p>
              </div>
            </div>
            <div className="bg-slate-50 px-5 py-3">
              <Link
                to="cross-instrument"
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                Compare instruments
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MetricsOverview;
